import React, { useEffect, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import GreenButton from '../home/GreenButton';
import type { ServiceOption } from '../../data/services/types';
import { snapAccordionItem } from '../../lib/snapAccordionItem';

interface OptionsAccordionProps {
  items: ServiceOption[];
}

const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

/**
 * Photo left, accordion right. One option open at a time with a spec table;
 * the photo cross-fades to match. Same motion as the All Signage accordion.
 */
const OptionsAccordion: React.FC<OptionsAccordionProps> = ({ items }) => {
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    items.forEach((s) => {
      const img = new Image();
      img.src = s.image;
    });
  }, [items]);

  const current = items[active] ?? items[0];

  const openItem = (i: number, row: HTMLElement) => {
    const rows = listRef.current?.children;
    const prevPanel = rows && active < i ? (rows[active] as HTMLElement).querySelector<HTMLElement>('[id^="option-panel-"]') : null;
    setActive(i);
    snapAccordionItem(row, prevPanel);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[5fr_6fr] gap-10 lg:gap-16 items-center">
      {/* Desktop: one photo panel that cross-fades. Mobile: each open item shows its own photo instead. */}
      <div className="relative overflow-hidden bg-black aspect-[4/5] hidden lg:block">
        {items.map((item, i) => (
          <img
            key={item.id}
            src={item.image}
            alt={item.title}
            className="absolute inset-0 w-full h-full object-cover will-change-transform"
            style={{
              opacity: i === active ? 1 : 0,
              transform: `scale(${i === active ? 1 : 1.06})`,
              transition: `opacity 600ms ${EASE}, transform 1200ms ${EASE}`,
            }}
            loading={i === 0 ? 'eager' : 'lazy'}
          />
        ))}
        <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
          <span className="font-poppins text-[11px] font-semibold tracking-[0.2em] uppercase text-[#78BE20]">
            {String(active + 1).padStart(2, '0')} / {String(items.length).padStart(2, '0')}
          </span>
          <p className="font-montserrat font-extrabold text-white text-xl mt-1">{current.title}</p>
        </div>
      </div>

      <ul ref={listRef} className="divide-y divide-black/10 border-y border-black/10">
        {items.map((item, i) => {
          const open = i === active;
          return (
            <li key={item.id}>
              <button
                type="button"
                onClick={(e) => openItem(i, e.currentTarget.parentElement as HTMLElement)}
                aria-expanded={open}
                aria-controls={`option-panel-${item.id}`}
                className="group w-full flex items-center gap-4 sm:gap-5 py-4 sm:py-5 text-left"
              >
                <span
                  className={`font-poppins text-[11px] font-semibold tracking-[0.2em] transition-colors duration-300 ${
                    open ? 'text-[#78BE20]' : 'text-black/40 group-hover:text-black/70'
                  }`}
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span
                  className={`flex-1 font-montserrat font-extrabold tracking-[-0.02em] text-[20px] sm:text-[22px] md:text-[26px] leading-tight transition-colors duration-300 ${
                    open ? 'text-black' : 'text-black/50 group-hover:text-black'
                  }`}
                >
                  {item.title}
                </span>
                <Plus
                  className={`w-5 h-5 shrink-0 transition-transform duration-500 ${
                    open ? 'rotate-45 text-[#78BE20]' : 'text-black/40 group-hover:text-black'
                  }`}
                  style={{ transitionTimingFunction: EASE }}
                />
              </button>

              <div
                id={`option-panel-${item.id}`}
                className="grid motion-reduce:transition-none"
                style={{
                  gridTemplateRows: open ? '1fr' : '0fr',
                  transition: `grid-template-rows 520ms ${EASE}`,
                }}
              >
                <div className="overflow-hidden">
                  <div
                    className="pb-6 sm:pb-7 pl-0 sm:pl-[calc(1.25rem+22px)] motion-reduce:transition-none"
                    style={{
                      opacity: open ? 1 : 0,
                      transform: `translateY(${open ? 0 : '-6px'})`,
                      transition: open
                        ? `opacity 400ms ${EASE} 160ms, transform 500ms ${EASE} 120ms`
                        : `opacity 150ms ${EASE}, transform 200ms ${EASE}`,
                    }}
                  >
                    <img
                      src={item.image}
                      alt={item.title}
                      loading="lazy"
                      className="lg:hidden w-full aspect-[16/10] object-cover bg-black mb-5"
                    />
                    {item.lead && <p className="font-poppins font-semibold text-[15px] sm:text-base">{item.lead}</p>}
                    {item.body && <p className="font-poppins text-[15px] leading-6 sm:text-base sm:leading-7 text-black/70 mt-2 sm:mt-3 max-w-xl">{item.body}</p>}
                    {item.specs && item.specs.length > 0 && (
                      <dl className="mt-5 grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-x-6 gap-y-0 sm:gap-y-2 max-w-xl">
                        {item.specs.map((s) => (
                          <React.Fragment key={s.label}>
                            <dt className="font-montserrat font-bold text-[12px] uppercase tracking-[0.05em] pt-0.5 mt-3 first:mt-0 sm:mt-0">
                              {s.label}
                            </dt>
                            <dd className="font-poppins text-sm leading-6 text-black/70 mt-0.5 sm:mt-0">{s.value}</dd>
                          </React.Fragment>
                        ))}
                      </dl>
                    )}
                    {item.buttons && item.buttons.length > 0 && (
                      <div className="mt-5 flex flex-wrap gap-3">
                        {item.buttons.map((b) => (
                          <GreenButton key={b.to} to={b.to} variant="outline" className="text-black">
                            {b.label}
                          </GreenButton>
                        ))}
                      </div>
                    )}
                    {!item.buttons && item.to && (
                      <div className="mt-5">
                        <GreenButton to={item.to} variant="outline" className="text-black">
                          {item.buttonText ?? 'Find out more'}
                        </GreenButton>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default OptionsAccordion;
